import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowRightIcon, CubeIcon, MapPinIcon, ShareNetworkIcon, SpeakerHighIcon, SpeakerSlashIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { HomeLogo } from '@/components/ui/home-logo';
import { IconButton } from '@/components/ui/icon-button';
import { TextButton, TextLink } from '@/components/ui/text-button';
import { CardTitle, Overline } from '@/components/ui/text';
import { Card } from '@/components/ui/card';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import logoLight from '@/assets/lureo_logo.png';

const HERO_VIDEO_SRC = '/creators-hero.mp4';
const HERO_POSTER = '/creators-hero-poster.jpg';

const STEPS = [
  {
    step: 1,
    title: 'Design in 3D',
    body: 'Shapes, text, your own models, motion and triggers — all in a clean browser editor.',
    icon: <CubeIcon size={22} />
  },
  {
    step: 2,
    title: 'Launch it in the world',
    body: 'Anchor a scene to a place or a surface, then view it in AR on any phone.',
    icon: <MapPinIcon size={22} />
  },
  {
    step: 3,
    title: 'Share a link',
    body: 'Publish to a link or QR code. Anyone can open it — no app to install.',
    icon: <ShareNetworkIcon size={22} />
  }
];

export default function CreatorLanding() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const goToSignup = () => navigate(`/signup${search}`, { state: { fromCreatorLanding: true } });
  const goToLogin = () => navigate(`/login${search}`, { state: { fromCreatorLanding: true } });

  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  // Under reduced motion the hero shows its poster until the sound button plays it
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (!reducedMotion) videoRef.current?.play?.().catch(() => {});
  }, [reducedMotion]);
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play?.().catch(() => {});
  };

  return (
    <div className="h-dvh overflow-x-hidden overflow-y-auto bg-lureo-page text-lureo-ink">
      <div className="md:mx-auto md:grid md:min-h-[88vh] md:max-w-300 md:grid-cols-[minmax(15rem,22rem)_1fr] md:items-center md:gap-[clamp(2rem,5vw,4rem)] md:px-[clamp(1.25rem,5vw,2.5rem)] md:py-[clamp(2rem,4vw,3rem)]">
        <div className="sticky top-0 z-0 h-[68dvh] w-full overflow-hidden bg-lureo-shade md:relative md:top-auto md:z-auto md:aspect-9/16 md:h-auto md:max-h-[80vh] md:rounded-lureo-radius-lg md:shadow-lureo-shadow-float">
          <video
            ref={videoRef}
            className="absolute inset-0 block size-full object-cover"
            src={HERO_VIDEO_SRC}
            poster={HERO_POSTER}
            autoPlay={!reducedMotion}
            muted
            loop
            playsInline
            preload="auto"
          />
          <Link to="/" aria-label="Lureo home" className="absolute left-[clamp(1.25rem,5vw,2rem)] top-[clamp(1.1rem,3vw,1.6rem)] z-10 block">
            <img src={logoLight} alt="Lureo" className="h-6 w-auto opacity-95 brightness-0 invert" />
          </Link>
          <IconButton
            place="overlay"
            onClick={toggleMute}
            className="absolute right-[clamp(1rem,4vw,1.5rem)] top-[clamp(1.1rem,3vw,1.6rem)] z-10"
            label={muted ? 'Unmute video' : 'Mute video'}
          >
            {muted ? <SpeakerSlashIcon size={18} weight="fill" /> : <SpeakerHighIcon size={18} weight="fill" />}
          </IconButton>
        </div>

        <div className="relative z-10 -mt-6 flex flex-col gap-4 corner-t-lureo-radius-xl bg-lureo-page px-[clamp(1.25rem,5vw,2rem)] pb-8 pt-7.5 shadow-lureo-shadow-sheet md:mt-0 md:rounded-none md:bg-transparent md:p-0 md:shadow-none">
          <Overline as="span">Spatial Stories. Made by you.</Overline>
          <h1 className="m-0 text-balance text-lureo-display font-bold leading-[1.05] tracking-[-0.02em] text-lureo-ink">
            Create Immersive Content
          </h1>
          <p className="m-0 max-w-[42ch] font-lureo-font-prose text-lureo-lead leading-[1.5] text-lureo-ink">
            Design Augmented Reality experiences in your browser, launch them in the real world, and share with a single link. No code. No headset.
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-4">
            <LureoButton variant="secondary" onClick={goToSignup} className="rounded-lureo-radius-button-pill px-6 py-3">
              <span>Start creating</span>
              <ArrowRightIcon size={17} weight="bold" />
            </LureoButton>
            <span className="text-lureo-14 text-lureo-ink-subtle">
              Already have an account?{' '}
              <TextButton onClick={goToLogin}>
                Sign in
              </TextButton>
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 bg-lureo-page">
        <main className="mx-auto w-full max-w-300 px-[clamp(1.25rem,5vw,2.5rem)] pb-[clamp(1.5rem,4vw,2rem)] pt-[clamp(1rem,3vw,2rem)]">
          <section>
            <div className="mb-4 flex min-h-9 items-baseline">
              <Overline as="span">
                How it works
                <span className="ml-2 font-normal tracking-normal tabular-nums text-lureo-ink-subtlest">3 steps</span>
              </Overline>
            </div>
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
              {STEPS.map((s) => (
                <Card
                  key={s.step}
                  role="article"
                  flat
                  className="flex flex-col gap-3 rounded-lureo-radius transition-transform duration-lureo-duration-moderate hover:-translate-y-0.5"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-lureo-brand-ink/10 text-lureo-brand-ink">
                    {s.icon}
                  </span>
                  <Overline as="span">Step {s.step}</Overline>
                  <CardTitle as="h3" className="m-0">{s.title}</CardTitle>
                  <p className="m-0 font-lureo-font-prose text-lureo-16 leading-[1.45] text-lureo-ink-subtle">{s.body}</p>
                </Card>
              ))}
            </div>
          </section>

          <footer className="mt-[clamp(2.5rem,6vw,4rem)] flex flex-wrap items-center justify-between gap-4 border-t-lureo-border-width border-lureo-line pb-6 pt-8">
            <HomeLogo href="/" label="Lureo home" size="sm" className="px-0" />
            <nav className="flex flex-wrap gap-6 text-lureo-14 text-lureo-ink-subtle">
              <TextLink tone="quiet" href="#" className="text-lureo-14 font-normal">Privacy</TextLink>
              <TextLink tone="quiet" href="#" className="text-lureo-14 font-normal">Terms</TextLink>
              <TextLink tone="quiet" href="https://lureo.app" className="text-lureo-14 font-normal">lureo.app</TextLink>
            </nav>
            <p className="m-0 w-full font-lureo-font-prose text-lureo-14 text-lureo-ink-subtlest">© 2026 Lureo. Create immersive content.</p>
          </footer>
        </main>
      </div>
    </div>
  );
}
